import React, { useState, useEffect, useRef } from 'react';
import { PrismBanner, PrismTabs, PrismTab } from '@prism-ui/react';
import ApprovalsTab from './ApprovalsTab';
import ReminderSettingsTab from './ReminderSettingsTab';
import ApprovalTypesTab from './ApprovalTypesTab';
import AdminPane from '../../AdminPane';
import './ManagerApprovalAdmin.css';

/*
 * prism-tabs requires each <prism-tab> to carry an `id` matching its Tab object's
 * `controls` value. Without it the first panel renders correctly and then every panel
 * disappears permanently on the first tab switch. See client/design-lab/prism-tabs-probe.html.
 */
const panelId = (id) => `manager-approval-panel-${id}`;

const TABS = [
  { id: 'approvals', title: 'Approvals', controls: panelId('approvals'), active: true },
  { id: 'reminders', title: 'Reminder Settings', controls: panelId('reminders') },
  { id: 'types', title: 'Approval Types', controls: panelId('types') },
];

const ManagerApprovalAdmin = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('approvals');
  // Tabs are mounted lazily but never unmounted. Rendering only the *active* tab meant
  // leaving a tab tore it down, so coming back remounted it — refetching its data and, for
  // ApprovalsTab, opening a brand new socket on every single click. prism-tabs hides the
  // inactive panels itself, so keeping visited children mounted costs nothing visually.
  const [visited, setVisited] = useState(() => new Set(['approvals']));
  const [configVersion, setConfigVersion] = useState(0);
  const tabsRef = useRef(null);

  // `tabs` is an array, so it has to be assigned as a DOM property; an attribute would
  // stringify it.
  useEffect(() => {
    if (tabsRef.current) {
      tabsRef.current.tabs = TABS;
    }
  }, []);

  const getBannerContent = () => {
    switch (activeTab) {
      case 'approvals':
        return (
          <>
            <strong>Manager Approvals:</strong>
            <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
              <li>View and manage manager approval status for all submitted applications</li>
              <li>Use the search box to filter by applicant name or manager email</li>
              <li>Override a manager's decision by clicking the "Override" button on any row</li>
              <li>Send ad-hoc reminder emails to individual managers or all pending managers at once</li>
              <li>Expand any row with a ▸ to view the full approval history</li>
            </ul>
          </>
        );
      case 'reminders':
        return (
          <>
            <strong>Reminder Settings:</strong>
            <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
              <li>Configure automated reminder emails sent to managers with pending approvals</li>
              <li>Set the frequency, start delay (days after submission), and stop window (days before close)</li>
              <li>Each chapter can have its own reminder schedule — enable or disable independently</li>
              <li>Use "Send Reminder Now" to immediately trigger a reminder blast for a specific chapter</li>
            </ul>
          </>
        );
      case 'types':
        return (
          <>
            <strong>Approval Types:</strong>
            <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
              <li>Control which applicant types require manager approval per chapter</li>
              <li>Mentee approval is enabled by default — Mentor and Committee types can be toggled on as needed</li>
              <li>Disabling a type removes those applicants from the manager's approval page and stops reminder emails for that type</li>
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <AdminPane as="div" width="full">
      <PrismBanner tone="info" layout="inline">
        {getBannerContent()}
      </PrismBanner>

      <PrismTabs
        ref={tabsRef}
        setAriaLabel="Manager approval settings"
        className="clr:block clr:mt-md"
        onPrismChange={(e) => {
          const detail = e.detail || {};
          if (detail.action === 'visible' && detail.tabId) {
            setActiveTab(detail.tabId);
            setVisited((prev) =>
              prev.has(detail.tabId) ? prev : new Set(prev).add(detail.tabId)
            );
          }
        }}
      >
        {/*
          Children mount on first visit and stay mounted. Each of these three tabs fetches on
          mount (ApprovalsTab also opens a socket), and prism-tabs mounts every panel
          eagerly, so rendering them unconditionally would fire all of their requests on
          page load instead of one tab's.
        */}
        <PrismTab tab={panelId('approvals')} id={panelId('approvals')}>
          {visited.has('approvals') && (
            <ApprovalsTab currentUser={currentUser} configVersion={configVersion} />
          )}
        </PrismTab>
        <PrismTab tab={panelId('reminders')} id={panelId('reminders')}>
          {visited.has('reminders') && <ReminderSettingsTab currentUser={currentUser} />}
        </PrismTab>
        <PrismTab tab={panelId('types')} id={panelId('types')}>
          {visited.has('types') && (
            <ApprovalTypesTab
              currentUser={currentUser}
              onConfigChange={() => setConfigVersion((v) => v + 1)}
            />
          )}
        </PrismTab>
      </PrismTabs>
    </AdminPane>
  );
};

export default ManagerApprovalAdmin;
